import React from 'react';
import { MediaQuery, useMediaQuery } from '@hooks';
import { FaceMatchingResponseData, PredictionResponse } from '@models/prediction';
import Config from '@utils/config';
import { HeaderTab } from '@components/commons';
import TimeoutCircularLoading, {
    DEFAULT_LOADING_MESSAGE,
    TIMEOUT_MESSAGE,
} from '@components/timeout-loading';
import { AgeAntispoofingResponse } from '@components/result/age-antispoofing';
import ResultCard, { ResultCardVariant } from '@components/result/card';
import FaceMatchingResponse from './FaceMatchingResponse';
import styles from './resultFaceMatching.module.scss';
import { Button } from '@yoti/ui-kit-components-react/button';

interface FaceMatchingImage {
    image: string;
    result?: PredictionResponse;
}

interface Props {
    firstImage: FaceMatchingImage;
    secondImage: FaceMatchingImage;
    result?: FaceMatchingResponseData;
    onBackButtonClick: () => void;
}

const ResultFaceMatching: React.FC<Props> = (props) => {
    const { firstImage, secondImage, result, onBackButtonClick } = props;

    const isLaptop = useMediaQuery(MediaQuery.lg);

    return (
        <div className={styles['result']} data-qa="result-face-matching">
            <HeaderTab className={styles['result__header']} tabs={['Result']} />
            <div className={styles['result__panel']}>
                <div className={styles['result__panel__card-container']}>
                    <ResultCard
                        variant={isLaptop ? ResultCardVariant.Bottom : ResultCardVariant.Right}
                        className={styles['result__panel__card-container__card']}
                        content={
                            <img
                                className={styles['result__panel__card-container__card__image']}
                                src={firstImage.image}
                                alt="face"
                                data-qa="first-image"
                            />
                        }
                        secondaryContent={
                            firstImage.result ? (
                                <AgeAntispoofingResponse responseData={firstImage.result} />
                            ) : undefined
                        }
                    />
                    <ResultCard
                        variant={isLaptop ? ResultCardVariant.Bottom : ResultCardVariant.Right}
                        className={styles['result__panel__card-container__card']}
                        content={
                            <img
                                className={styles['result__panel__card-container__card__image']}
                                src={secondImage.image}
                                alt="face"
                                data-qa="second-image"
                            />
                        }
                        secondaryContent={
                            secondImage.result ? (
                                <AgeAntispoofingResponse responseData={secondImage.result} />
                            ) : undefined
                        }
                    />
                </div>

                <div className={styles['result__panel__response']}>
                    {result ? (
                        <FaceMatchingResponse responseData={result} />
                    ) : (
                        <TimeoutCircularLoading
                            className={styles['result__panel__response__loading']}
                            message={DEFAULT_LOADING_MESSAGE}
                            timeoutMessage={TIMEOUT_MESSAGE}
                            timeout={Config.slowRequestNotificationTime}
                        />
                    )}
                </div>
                <div className={styles['result__panel__back-button']}>
                    <Button onClick={onBackButtonClick}>Back</Button>
                </div>
            </div>
        </div>
    );
};

export default ResultFaceMatching;
